import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch, apiUrl, resolveMediaUrl } from '@/lib/api';
import { PcRecommendedCallout } from '@/components/PcRecommendedCallout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { PageSetupProgressCard } from '@/components/PageSetupProgressCard';
import { SiteParentInheritBanner, type SiteInheritMeta } from '@/components/SiteParentInheritBanner';
import { inputClass, labelClass, settingsPanelClass } from '@/components/ui/inputStyles';
import { useSession } from '@/hooks/useSession';

const textareaClass = inputClass + ' resize-y';

const FIXED_KEYS = [
  { key: 'body', label: '正文 / 描述', hint: '映射到 {body}' },
  { key: 'contact_phone', label: '联系电话', hint: '映射到 {contact_phone}' },
  {
    key: 'images',
    label: '图片（多图）',
    hint: '填写该表单里「图片/附件」对应的 field_xx；金数据多为 URL 字符串数组（如 field_2: [下载链接, …]）',
  },
] as const;

/** 新建站点或未填时的推荐默认（与 create_site / install.sql 一致） */
const DEFAULT_JINSHU_FIXED: Record<(typeof FIXED_KEYS)[number]['key'], string> = {
  body: 'field_1',
  contact_phone: 'field_3',
  images: 'field_2',
};

type ExtraRow = { _id: string; alias: string; fieldKey: string };

type DraftMode = 'news' | 'newspic';

type TitleMode = 'template' | 'ai';

type Resp = {
  ok: boolean;
  site_id: number;
  field_map_json: string;
  field_map: Record<string, string>;
  draft_title_template: string;
  draft_html_template: string;
  default_valid_days: number;
  draft_mode?: DraftMode;
  newspic_custom_head_enabled?: boolean;
  newspic_custom_tail_enabled?: boolean;
  newspic_custom_head_path?: string;
  newspic_custom_tail_path?: string;
  newspic_portrait_34_enabled?: boolean;
  draft_title_mode?: TitleMode;
  draft_title_ai_prompt?: string;
  entry_title_mode?: TitleMode;
  entry_title_template?: string;
  entry_title_ai_prompt?: string;
  draft_body_prefix_enabled?: boolean;
  draft_body_prefix_text?: string;
  draft_body_suffix_enabled?: boolean;
  draft_body_suffix_text?: string;
  sensitive_words?: string;
  hint?: string;
  draft_miniprogram_id?: number | null;
  /** 小程序文字链可见文案；空表示使用默认「打开小程序查看本条」 */
  draft_miniprogram_anchor_text?: string;
  miniprograms_for_draft?: Array<{
    id: number;
    name: string;
    enabled: number;
    appid: string;
    appid_ready: boolean;
  }>;
  /** 小程序首页「转发给好友」等使用的分享图（HTTPS）；空则用小程序管理里全局配图 */
  minipro_home_share_image_path?: string;
  inherit?: SiteInheritMeta;
};

const TITLE_AI_EXTRA_PLACEHOLDER =
  '（可选）若需额外强调本站规范，可在此填写。留空则仅使用系统内置规则：抽取地点、物品、数量、价格等核心信息；总长度不超过 20 字。';
const TITLE_SCENE_PRESETS = {
  resale: {
    template: '{body}',
    aiPrompt: '优先提取小区/地点、物品名、数量和价格；强调二手交易场景，避免夸张词。',
  },
  hiring: {
    template: '{form_name}{body}',
    aiPrompt: '优先提取岗位、薪资、地点与招聘类型；标题风格正规简洁，不要口号化。',
  },
  marriage: {
    template: '{body}',
    aiPrompt: '优先提取征婚核心要素（地区、年龄段、基本条件），用语得体、克制、真实。',
  },
} as const;

function newRowId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random();
}

function mapToState(fieldMap: Record<string, string>): {
  fixed: Record<string, string>;
  extras: ExtraRow[];
} {
  const fixed: Record<string, string> = {
    body: '',
    contact_phone: '',
    images: '',
  };
  const fixedSet = new Set(FIXED_KEYS.map((x) => x.key));
  const extras: ExtraRow[] = [];
  for (const [k, v] of Object.entries(fieldMap || {})) {
    if (fixedSet.has(k)) {
      fixed[k] = v;
    } else {
      extras.push({ _id: newRowId(), alias: k, fieldKey: v });
    }
  }
  return { fixed, extras };
}

function stateToFieldMap(fixed: Record<string, string>, extras: ExtraRow[]): Record<string, string> {
  const o: Record<string, string> = {};
  for (const k of FIXED_KEYS.map((x) => x.key)) {
    const v = fixed[k]?.trim() ?? '';
    if (v !== '') o[k] = v;
  }
  for (const r of extras) {
    const a = r.alias.trim();
    const f = r.fieldKey.trim();
    if (a !== '' && f !== '') o[a] = f;
  }
  return o;
}

export function ContentSettingsPage() {
  const { currentSiteId, loading: sessionLoading } = useSession();
  const [apiLoading, setApiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [fixed, setFixed] = useState<Record<string, string>>({
    body: '',
    contact_phone: '',
    images: '',
  });
  const [extras, setExtras] = useState<ExtraRow[]>([]);
  const [titleTpl, setTitleTpl] = useState('');
  const [htmlTpl, setHtmlTpl] = useState('');
  const [days, setDays] = useState(7);
  const [draftMode, setDraftMode] = useState<DraftMode>('newspic');
  const [newspicPortrait34, setNewspicPortrait34] = useState(false);
  const [headCustomEn, setHeadCustomEn] = useState(false);
  const [tailCustomEn, setTailCustomEn] = useState(false);
  const [headPath, setHeadPath] = useState('');
  const [tailPath, setTailPath] = useState('');
  const [uploadHeadBusy, setUploadHeadBusy] = useState(false);
  const [uploadTailBusy, setUploadTailBusy] = useState(false);
  const [miniproHomeSharePath, setMiniproHomeSharePath] = useState('');
  const [uploadMiniproShareBusy, setUploadMiniproShareBusy] = useState(false);
  const [titleMode, setTitleMode] = useState<TitleMode>('template');
  const [titleAiPrompt, setTitleAiPrompt] = useState('');
  const [draftTitlePreset, setDraftTitlePreset] = useState<'resale' | 'hiring' | 'marriage'>('resale');
  const [entryTitleMode, setEntryTitleMode] = useState<TitleMode>('template');
  const [entryTitleTpl, setEntryTitleTpl] = useState('{body}');
  const [entryTitleAiPrompt, setEntryTitleAiPrompt] = useState('');
  const [entryTitlePreset, setEntryTitlePreset] = useState<'resale' | 'hiring' | 'marriage'>('resale');
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillAllBusy, setBackfillAllBusy] = useState(false);
  const [backfillLimit, setBackfillLimit] = useState(200);
  const [singleBackfillBusy, setSingleBackfillBusy] = useState(false);
  const [singleBackfillEntryId, setSingleBackfillEntryId] = useState(0);
  const [execLogs, setExecLogs] = useState<string[]>([]);
  const [bodyPrefixEn, setBodyPrefixEn] = useState(false);
  const [bodySuffixEn, setBodySuffixEn] = useState(false);
  const [bodyPrefixText, setBodyPrefixText] = useState('');
  const [bodySuffixText, setBodySuffixText] = useState('');
  const [sensitiveWords, setSensitiveWords] = useState('');
  const [draftMiniprogramId, setDraftMiniprogramId] = useState<number>(0);
  const [draftMiniprogramAnchorText, setDraftMiniprogramAnchorText] = useState('');
  const [miniprogramsForDraft, setMiniprogramsForDraft] = useState<NonNullable<Resp['miniprograms_for_draft']>>([]);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [jsonOverride, setJsonOverride] = useState('');
  const [inheritMeta, setInheritMeta] = useState<SiteInheritMeta | null>(null);

  const builtMap = useMemo(() => stateToFieldMap(fixed, extras), [fixed, extras]);

  function appendExecLog(line: string) {
    const ts = new Date().toLocaleTimeString();
    setExecLogs((prev) => {
      const next = [...prev, `[${ts}] ${line}`];
      return next.length > 600 ? next.slice(next.length - 600) : next;
    });
  }

  const load = useCallback(
    async (opts?: { quiet?: boolean }) => {
      if (currentSiteId < 1) return;
      if (!opts?.quiet) setApiLoading(true);
      setErr('');
      try {
        const r = await apiFetch<Resp>('get_site_content_settings', {
          method: 'GET',
          query: { site_id: currentSiteId },
        });
        if (r.ok) {
          const fm = r.field_map && typeof r.field_map === 'object' ? r.field_map : {};
          const s = mapToState(fm as Record<string, string>);
          setFixed(s.fixed);
          setExtras(s.extras);
          setTitleTpl(r.draft_title_template);
          setHtmlTpl(r.draft_html_template);
          setDays(r.default_valid_days);
          setDraftMode(r.draft_mode === 'news' ? 'news' : 'newspic');
          setNewspicPortrait34(Boolean(r.newspic_portrait_34_enabled));
          setHeadCustomEn(Boolean(r.newspic_custom_head_enabled));
          setTailCustomEn(Boolean(r.newspic_custom_tail_enabled));
          setHeadPath(r.newspic_custom_head_path ?? '');
          setTailPath(r.newspic_custom_tail_path ?? '');
          setTitleMode(r.draft_title_mode === 'ai' ? 'ai' : 'template');
          setTitleAiPrompt(r.draft_title_ai_prompt ?? '');
          setEntryTitleMode(r.entry_title_mode === 'ai' ? 'ai' : 'template');
          setEntryTitleTpl(r.entry_title_template ?? '{body}');
          setEntryTitleAiPrompt(r.entry_title_ai_prompt ?? '');
          setBodyPrefixEn(Boolean(r.draft_body_prefix_enabled));
          setBodySuffixEn(Boolean(r.draft_body_suffix_enabled));
          setBodyPrefixText(r.draft_body_prefix_text ?? '');
          setBodySuffixText(r.draft_body_suffix_text ?? '');
          setSensitiveWords(r.sensitive_words ?? '');
          const dmp = r.draft_miniprogram_id;
          setDraftMiniprogramId(typeof dmp === 'number' && dmp > 0 ? dmp : 0);
          setDraftMiniprogramAnchorText(typeof r.draft_miniprogram_anchor_text === 'string' ? r.draft_miniprogram_anchor_text : '');
          setMiniprogramsForDraft(Array.isArray(r.miniprograms_for_draft) ? r.miniprograms_for_draft : []);
          setMiniproHomeSharePath(r.minipro_home_share_image_path ?? '');
          setInheritMeta(r.inherit ?? null);
          setAdvancedOpen(false);
          setJsonOverride('');
        } else {
          setErr('加载失败');
        }
      } catch {
        setErr('加载失败');
      } finally {
        if (!opts?.quiet) setApiLoading(false);
      }
    },
    [currentSiteId],
  );

  useEffect(() => {
    if (sessionLoading) return;
    void load();
  }, [sessionLoading, load]);

  async function save() {
    if (currentSiteId < 1) return;
    let fieldMapPayload: Record<string, string>;
    if (advancedOpen && jsonOverride.trim() !== '') {
      try {
        const p = JSON.parse(jsonOverride) as unknown;
        if (typeof p !== 'object' || p === null || Array.isArray(p)) {
          setErr('JSON 必须是对象');
          return;
        }
        const raw = p as Record<string, unknown>;
        fieldMapPayload = {};
        for (const [k, v] of Object.entries(raw)) {
          if (typeof v === 'string' && k.trim() !== '' && v.trim() !== '') {
            fieldMapPayload[k.trim()] = v.trim();
          }
        }
      } catch {
        setErr('JSON 解析失败');
        return;
      }
    } else {
      fieldMapPayload = builtMap;
    }
    if (Object.keys(fieldMapPayload).length === 0) {
      setErr('请至少填写一个字段映射');
      return;
    }
    setSaving(true);
    setMsg('');
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean }>('save_site_content_settings', {
        method: 'POST',
        body: JSON.stringify({
          site_id: currentSiteId,
          field_map: fieldMapPayload,
          draft_title_template: titleTpl,
          draft_html_template: htmlTpl,
          default_valid_days: days,
          draft_mode: draftMode,
          newspic_portrait_34_enabled: newspicPortrait34,
          newspic_custom_head_enabled: headCustomEn,
          newspic_custom_tail_enabled: tailCustomEn,
          newspic_custom_head_path: headPath,
          newspic_custom_tail_path: tailPath,
          draft_title_mode: titleMode,
          draft_title_ai_prompt: titleAiPrompt,
          entry_title_mode: entryTitleMode,
          entry_title_template: entryTitleTpl,
          entry_title_ai_prompt: entryTitleAiPrompt,
          draft_body_prefix_enabled: bodyPrefixEn,
          draft_body_suffix_enabled: bodySuffixEn,
          draft_body_prefix_text: bodyPrefixText,
          draft_body_suffix_text: bodySuffixText,
          sensitive_words: sensitiveWords,
          draft_miniprogram_id: draftMiniprogramId > 0 ? draftMiniprogramId : null,
          draft_miniprogram_anchor_text: draftMiniprogramAnchorText,
          minipro_home_share_image_path: miniproHomeSharePath,
        }),
      });
      if (r.ok) {
        setMsg('已保存');
        await load({ quiet: true });
      } else setErr('保存失败');
    } catch {
      setErr('保存失败');
    } finally {
      setSaving(false);
    }
  }

  async function uploadMiniproHomeShare(file: File) {
    if (currentSiteId < 1) return;
    setUploadMiniproShareBusy(true);
    setErr('');
    try {
      const fd = new FormData();
      fd.append('site_id', String(currentSiteId));
      fd.append('slot', 'minipro_home_share');
      fd.append('file', file);
      const url = apiUrl('upload_newspic_custom_asset', {});
      const res = await fetch(url, { method: 'POST', body: fd, credentials: 'include' });
      const text = (await res.text()).replace(/^\uFEFF/, '').trim();
      const parsed = JSON.parse(text) as { ok?: boolean; local_path?: string; err?: string };
      if (parsed.ok && parsed.local_path) {
        setMiniproHomeSharePath(parsed.local_path);
        setMsg('首页分享图已上传，请点击「保存当前站点」写入');
      } else {
        setErr(parsed.err || '上传失败');
      }
    } catch {
      setErr('上传失败');
    } finally {
      setUploadMiniproShareBusy(false);
    }
  }

  async function uploadNewspicAsset(slot: 'head' | 'tail', file: File) {
    if (currentSiteId < 1) return;
    if (slot === 'head') setUploadHeadBusy(true);
    else setUploadTailBusy(true);
    setErr('');
    try {
      const fd = new FormData();
      fd.append('site_id', String(currentSiteId));
      fd.append('slot', slot);
      fd.append('file', file);
      const url = apiUrl('upload_newspic_custom_asset', {});
      const res = await fetch(url, { method: 'POST', body: fd, credentials: 'include' });
      const text = (await res.text()).replace(/^\uFEFF/, '').trim();
      const parsed = JSON.parse(text) as { ok?: boolean; local_path?: string; err?: string };
      if (parsed.ok && parsed.local_path) {
        if (slot === 'head') {
          setHeadPath(parsed.local_path);
          setHeadCustomEn(true);
        } else {
          setTailPath(parsed.local_path);
          setTailCustomEn(true);
        }
        setMsg('图片已上传，请点击「保存当前站点」写入配置');
      } else {
        setErr(parsed.err || '上传失败');
      }
    } catch {
      setErr('上传失败');
    } finally {
      if (slot === 'head') setUploadHeadBusy(false);
      else setUploadTailBusy(false);
    }
  }

  function addExtraRow() {
    setExtras((x) => [...x, { _id: newRowId(), alias: '', fieldKey: '' }]);
  }

  function removeExtra(id: string) {
    setExtras((x) => x.filter((r) => r._id !== id));
  }

  function applyTitlePreset(target: 'draft' | 'entry', preset: 'resale' | 'hiring' | 'marriage') {
    const p = TITLE_SCENE_PRESETS[preset];
    if (target === 'draft') {
      setTitleTpl(p.template);
      setTitleAiPrompt(p.aiPrompt);
      setDraftTitlePreset(preset);
      setMsg('已应用草稿标题预设（请点击保存当前站点）');
      return;
    }
    setEntryTitleTpl(p.template);
    setEntryTitleAiPrompt(p.aiPrompt);
    setEntryTitlePreset(preset);
    setMsg('已应用信息标题预设（请点击保存当前站点）');
  }

  async function backfillEntryTitles() {
    if (currentSiteId < 1 || backfillBusy) return;
    setBackfillBusy(true);
    setErr('');
    setMsg('');
    setExecLogs([]);
    const per = Math.min(1000, Math.max(1, backfillLimit || 200));
    appendExecLog(`开始单批补全，limit=${per}`);
    try {
      const r = await apiFetch<{
        ok: boolean;
        updated?: number;
        scanned?: number;
        skipped?: number;
        trace_counts?: Record<string, number>;
        logs?: Array<{ entry_id?: number; trace?: string; title?: string; detail?: string }>;
        err?: string;
        detail?: string;
      }>(
        'backfill_missing_entry_titles',
        {
          method: 'POST',
          body: JSON.stringify({
            site_id: currentSiteId,
            limit: per,
            debug: true,
          }),
        }
      );
      if (!r.ok) {
        setErr('补全失败：' + (r.detail || r.err || 'unknown'));
        appendExecLog(`失败：${r.detail || r.err || 'unknown'}`);
        return;
      }
      appendExecLog(
        `完成：扫描 ${r.scanned ?? 0}，更新 ${r.updated ?? 0}，跳过 ${r.skipped ?? 0}，trace=${JSON.stringify(r.trace_counts || {})}`
      );
      (r.logs || []).forEach((it) => {
        appendExecLog(`entry=${it.entry_id || 0} trace=${it.trace || ''} title=${it.title || ''} ${it.detail || ''}`.trim());
      });
      setMsg(`补全完成：扫描 ${r.scanned ?? 0} 条，更新 ${r.updated ?? 0} 条，跳过 ${r.skipped ?? 0} 条`);
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? `补全失败：${ex.message}` : '补全失败');
      appendExecLog(ex instanceof Error ? `异常：${ex.message}` : '异常：unknown');
    } finally {
      setBackfillBusy(false);
    }
  }

  async function backfillEntryTitlesAll() {
    if (currentSiteId < 1 || backfillAllBusy) return;
    setBackfillAllBusy(true);
    setErr('');
    setMsg('');
    setExecLogs([]);
    const per = Math.min(1000, Math.max(1, backfillLimit || 200));
    appendExecLog(`开始全量补全，batch_limit=${per}`);
    try {
      let beforeId = 0;
      let totalScanned = 0;
      let totalUpdated = 0;
      let totalSkipped = 0;
      for (let round = 0; round < 200; round++) {
        const r = await apiFetch<{
          ok: boolean;
          updated?: number;
          scanned?: number;
          skipped?: number;
          next_before_id?: number;
          has_more?: boolean;
          trace_counts?: Record<string, number>;
          logs?: Array<{ entry_id?: number; trace?: string; title?: string; detail?: string }>;
          err?: string;
          detail?: string;
        }>('backfill_missing_entry_titles', {
          method: 'POST',
          body: JSON.stringify({
            site_id: currentSiteId,
            limit: per,
            before_id: beforeId > 0 ? beforeId : undefined,
            debug: true,
          }),
        });
        if (!r.ok) {
          setErr('全量补全失败：' + (r.detail || r.err || 'unknown'));
          appendExecLog(`批次失败：${r.detail || r.err || 'unknown'}`);
          return;
        }
        const scanned = Number(r.scanned) || 0;
        totalScanned += scanned;
        totalUpdated += Number(r.updated) || 0;
        totalSkipped += Number(r.skipped) || 0;
        appendExecLog(
          `批次#${round + 1} scanned=${scanned} updated=${Number(r.updated) || 0} skipped=${Number(r.skipped) || 0} next_before_id=${Number(r.next_before_id) || 0} trace=${JSON.stringify(r.trace_counts || {})}`
        );
        (r.logs || []).slice(0, 60).forEach((it) => {
          appendExecLog(`entry=${it.entry_id || 0} trace=${it.trace || ''} title=${it.title || ''} ${it.detail || ''}`.trim());
        });
        const next = Number(r.next_before_id) || 0;
        if (!scanned || !next || scanned < per || !r.has_more) {
          break;
        }
        beforeId = next;
      }
      setMsg(`全量补全完成：扫描 ${totalScanned} 条，更新 ${totalUpdated} 条，跳过 ${totalSkipped} 条`);
      appendExecLog(`全量完成：扫描 ${totalScanned}，更新 ${totalUpdated}，跳过 ${totalSkipped}`);
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? `全量补全失败：${ex.message}` : '全量补全失败');
      appendExecLog(ex instanceof Error ? `异常：${ex.message}` : '异常：unknown');
    } finally {
      setBackfillAllBusy(false);
    }
  }

  async function backfillSingleEntryTitle() {
    if (currentSiteId < 1 || singleBackfillBusy) return;
    const entryId = Math.max(0, Number(singleBackfillEntryId) || 0);
    if (entryId < 1) {
      setErr('请先输入有效的 entry_id');
      return;
    }
    setSingleBackfillBusy(true);
    setErr('');
    setMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; updated?: boolean; detail?: string; title_text?: string; err?: string; trace?: string }>(
        'backfill_single_entry_title',
        {
          method: 'POST',
          body: JSON.stringify({
            site_id: currentSiteId,
            entry_id: entryId,
          }),
        }
      );
      if (!r.ok) {
        setErr('单条补全失败：' + (r.detail || r.err || 'unknown'));
        appendExecLog(`单条失败 entry=${entryId} ${r.detail || r.err || 'unknown'}`);
        return;
      }
      if (r.updated) {
        setMsg(`单条补全成功：entry_id=${entryId}，标题=${r.title_text || ''}`);
        appendExecLog(`单条成功 entry=${entryId} trace=${r.trace || ''} title=${r.title_text || ''}`);
      } else {
        setMsg(`单条补全完成：entry_id=${entryId}，${r.detail || '未更新'}`);
        appendExecLog(`单条未更新 entry=${entryId} trace=${r.trace || ''} detail=${r.detail || ''}`);
      }
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? `单条补全失败：${ex.message}` : '单条补全失败');
    } finally {
      setSingleBackfillBusy(false);
    }
  }

  const showMain = !sessionLoading && currentSiteId >= 1 && !apiLoading;

  return (
    <div className="space-y-6">
      <PageHeader
        title="内容与样式"
        description="将金数据字段（field_xx）映射到正文、电话、多图等；再编辑 HTML 模板与草稿体裁。接收 Webhook、推公众号草稿、AI 标题等总开关在侧栏「接入与推送」→「流程与开关」。"
      />

      <PcRecommendedCallout>
        字段映射与 HTML 模板编辑区域较大，建议在电脑浏览器中完成配置。
      </PcRecommendedCallout>

      {showMain ? (
        <SiteParentInheritBanner
          scope="content"
          siteId={currentSiteId}
          inherit={inheritMeta}
          onRestored={() => void load({ quiet: true })}
        />
      ) : null}

      <PageSetupProgressCard sessionLoading={sessionLoading} currentSiteId={currentSiteId} apiLoading={apiLoading} />

      {showMain ? (
        <>
      <Card title="公众号草稿体裁">
        <fieldset className="mb-4 space-y-2 border-0 p-0">
          <legend className="mb-2 text-sm font-medium text-slate-800">草稿类型</legend>
          <div className="space-y-2">
            <label className="flex cursor-pointer items-start gap-2">
              <input
                type="radio"
                name="draft_mode"
                className="mt-1 h-4 w-4 border-slate-300 text-teal-600 focus:ring-teal-500"
                checked={draftMode === 'newspic'}
                onChange={() => setDraftMode('newspic')}
              />
              <span>
                <span className="font-medium text-slate-800">贴图（newspic）</span>
                <span className="mt-0.5 block text-xs text-slate-500">
                  与公众平台「新的创作 → 贴图」一致：可传最多 20 张图，正文由下方 HTML 模板渲染后去掉标签变为纯文本。
                </span>
              </span>
            </label>
            {draftMode === 'newspic' ? (
              <div className="ml-7 space-y-2 border-l-2 border-teal-100 pl-3">
                <label className="flex cursor-pointer items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    checked={newspicPortrait34}
                    onChange={(e) => setNewspicPortrait34(e.target.checked)}
                  />
                  <span>
                    <span className="text-sm font-medium text-slate-800">首图强制竖屏（贴图模式竖屏展示区域更大）</span>
                    <span className="mt-0.5 block text-xs text-slate-500">
                      开启后，推送公众号贴图草稿时：每张图上传微信素材库前会先按 EXIF 转正；若为横版则旋转为竖版；再居中截取{' '}
                      <strong className="text-slate-700">3:4</strong>（宽:高）区域并压缩后上传。适用于条目图及自定义首/尾图。
                    </span>
                  </span>
                </label>
              </div>
            ) : null}
          </div>
          <label className="flex cursor-pointer items-start gap-2">
            <input
              type="radio"
              name="draft_mode"
              className="mt-1 h-4 w-4 border-slate-300 text-teal-600 focus:ring-teal-500"
              checked={draftMode === 'news'}
              onChange={() => setDraftMode('news')}
            />
            <span>
              <span className="font-medium text-slate-800">普通图文（news）</span>
              <span className="mt-0.5 block text-xs text-slate-500">
                经典单图文草稿：仅首张图作为封面，正文保留 HTML（需使用微信支持的图文内图片 URL 规则）。
              </span>
            </span>
          </label>
        </fieldset>
      </Card>

      <Card title="小程序：首页转发分享图">
        <p className="mb-3 text-xs text-slate-600">
          用户在小程序<strong className="text-slate-800">首页</strong>按当前筛选站点转发给好友或朋友圈时，优先使用本图作为分享封面；未配置时沿用「小程序管理」里的全局转发配图。一级下的二级站点可分别配置。
        </p>
        <div className={settingsPanelClass}>
          <div className="mt-1 flex flex-wrap items-end gap-3">
            <label className="block">
              <span className="mb-1 block text-xs text-slate-500">上传图片（建议 5:4）</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="max-w-full text-sm"
                disabled={uploadMiniproShareBusy || currentSiteId < 1}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void uploadMiniproHomeShare(f);
                }}
              />
            </label>
            {uploadMiniproShareBusy ? <span className="text-xs text-slate-500">上传中…</span> : null}
            {miniproHomeSharePath ? (
              <div className="flex flex-wrap items-center gap-2">
                <img
                  src={resolveMediaUrl(miniproHomeSharePath)}
                  alt=""
                  className="h-20 w-[100px] rounded-lg border border-slate-200 object-cover"
                />
                <code className="max-w-[240px] truncate text-[10px] text-slate-500">{miniproHomeSharePath}</code>
                <button
                  type="button"
                  className="text-xs text-rose-600 hover:underline"
                  onClick={() => {
                    setMiniproHomeSharePath('');
                    setMsg('已清除本地路径，请点击「保存当前站点」生效');
                  }}
                >
                  清除
                </button>
              </div>
            ) : (
              <span className="text-xs text-slate-500">未配置（使用小程序全局图）</span>
            )}
          </div>
        </div>
      </Card>

      <Card title="正文：自定义开头 / 结尾">
        <p className="mb-4 text-xs text-slate-500">
          推送公众号草稿时：「自定义开头」仍在模板正文<strong className="text-slate-700">之前</strong>；「自定义结尾」在最终草稿里排在
          <strong className="text-slate-700">小程序文字链之后</strong>（贴图体裁为纯文本单换行衔接；普通图文为独立段落，且若模板中未写{' '}
          <code className="rounded bg-slate-50 px-1">{`{miniprogram_link_html}`}</code>
          ，系统会在结尾段之前自动插入小程序链）。段与段之间<strong className="text-slate-700">不再额外多空行</strong>，避免正文与结尾之间出现大块空白。
        </p>
        <div className="space-y-6">
          <div className={settingsPanelClass}>
            <label className="flex cursor-pointer items-start gap-2">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                checked={bodyPrefixEn}
                onChange={(e) => setBodyPrefixEn(e.target.checked)}
              />
              <span className="text-sm font-medium text-slate-800">开启：正文-自定义开头</span>
            </label>
            {bodyPrefixEn ? (
              <textarea
                className={textareaClass + ' mt-3 min-h-[88px] text-sm'}
                value={bodyPrefixText}
                onChange={(e) => setBodyPrefixText(e.target.value)}
                placeholder="纯文本，将出现在正文最前"
                spellCheck={false}
              />
            ) : null}
          </div>
          <div className={settingsPanelClass}>
            <label className="flex cursor-pointer items-start gap-2">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                checked={bodySuffixEn}
                onChange={(e) => setBodySuffixEn(e.target.checked)}
              />
              <span className="text-sm font-medium text-slate-800">开启：正文-自定义结尾</span>
            </label>
            {bodySuffixEn ? (
              <textarea
                className={textareaClass + ' mt-3 min-h-[88px] text-sm'}
                value={bodySuffixText}
                onChange={(e) => setBodySuffixText(e.target.value)}
                placeholder="纯文本；贴图/图文均排在小程序入口之后"
                spellCheck={false}
              />
            ) : null}
          </div>
        </div>
      </Card>

      {draftMode === 'newspic' ? (
        <Card title="贴图：自定义首图 / 尾图">
          <p className="mb-4 text-xs text-slate-500">
            开启后，提交公众号贴图草稿时会在<strong className="text-slate-700">金数据图片列表之前插入首图</strong>、
            <strong className="text-slate-700">之后追加尾图</strong>。三者合计最多 20 张，超出时优先保留首/尾，中间条目图会被截断。
          </p>
          <p className="mb-4 rounded-lg border border-dashed border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
            <span className="font-medium text-slate-700">建议</span>：使用<strong className="text-slate-800">竖版</strong>图片，宽高比约{' '}
            <strong className="font-mono text-slate-800">3:4</strong>（宽:高），与贴图在公众号内的展示更一致。
          </p>
          <div className="space-y-6">
            <div className={settingsPanelClass}>
              <label className="flex cursor-pointer items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  checked={headCustomEn}
                  onChange={(e) => setHeadCustomEn(e.target.checked)}
                />
                <span className="text-sm font-medium text-slate-800">开启：自定义首图</span>
              </label>
              <div className="mt-3 flex flex-wrap items-end gap-3">
                <label className="block">
                  <span className="mb-1 block text-xs text-slate-500">上传首图</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="max-w-full text-sm"
                    disabled={uploadHeadBusy || currentSiteId < 1}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (f) void uploadNewspicAsset('head', f);
                    }}
                  />
                </label>
                {uploadHeadBusy ? <span className="text-xs text-slate-500">上传中…</span> : null}
                {headPath ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <img
                      src={resolveMediaUrl(headPath)}
                      alt=""
                      className="h-16 w-auto max-w-[120px] rounded border border-slate-200 object-contain"
                    />
                    <code className="max-w-[240px] truncate text-[10px] text-slate-500">{headPath}</code>
                    <button
                      type="button"
                      className="text-xs text-rose-600 hover:underline"
                      onClick={() => {
                        setHeadPath('');
                        setHeadCustomEn(false);
                      }}
                    >
                      清除
                    </button>
                  </div>
                ) : (
                  <span className="text-xs text-amber-800">未上传</span>
                )}
              </div>
            </div>

            <div className={settingsPanelClass}>
              <label className="flex cursor-pointer items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  checked={tailCustomEn}
                  onChange={(e) => setTailCustomEn(e.target.checked)}
                />
                <span className="text-sm font-medium text-slate-800">开启：自定义尾图</span>
              </label>
              <div className="mt-3 flex flex-wrap items-end gap-3">
                <label className="block">
                  <span className="mb-1 block text-xs text-slate-500">上传尾图</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="max-w-full text-sm"
                    disabled={uploadTailBusy || currentSiteId < 1}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (f) void uploadNewspicAsset('tail', f);
                    }}
                  />
                </label>
                {uploadTailBusy ? <span className="text-xs text-slate-500">上传中…</span> : null}
                {tailPath ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <img
                      src={resolveMediaUrl(tailPath)}
                      alt=""
                      className="h-16 w-auto max-w-[120px] rounded border border-slate-200 object-contain"
                    />
                    <code className="max-w-[240px] truncate text-[10px] text-slate-500">{tailPath}</code>
                    <button
                      type="button"
                      className="text-xs text-rose-600 hover:underline"
                      onClick={() => {
                        setTailPath('');
                        setTailCustomEn(false);
                      }}
                    >
                      清除
                    </button>
                  </div>
                ) : (
                  <span className="text-xs text-amber-800">未上传</span>
                )}
              </div>
            </div>
          </div>
        </Card>
      ) : null}

      <Card title="金数据字段映射">
        <p className="mb-4 text-xs text-slate-500">
          在金数据表单编辑器中查看各字段的 API 名（一般为 field_数字）。图片字段填写后，服务端会按顺序下载到本地。
          {draftMode === 'newspic'
            ? ' 推送贴图草稿时会上传全部图片（首张为封面，最多 20 张）。'
            : ' 推送普通图文草稿时仅使用第一张作为封面。'}
        </p>
        <div className="space-y-4">
          {FIXED_KEYS.map((row) => (
            <div key={row.key} className="grid gap-2 sm:grid-cols-[140px_1fr] sm:items-center">
              <div>
                <span className="text-sm font-medium text-slate-800">{row.label}</span>
                <span className="mt-0.5 block text-[11px] text-slate-400">{row.hint}</span>
              </div>
              <input
                className={inputClass + ' font-mono text-sm'}
                placeholder={`例如 ${DEFAULT_JINSHU_FIXED[row.key]}`}
                value={fixed[row.key] ?? ''}
                onChange={(e) => setFixed((f) => ({ ...f, [row.key]: e.target.value }))}
                spellCheck={false}
              />
            </div>
          ))}
        </div>

        <div className="mt-6 border-t border-slate-100 pt-4">
          <p className="mb-2 text-sm font-medium text-slate-800">自定义映射</p>
          <p className="mb-3 text-xs text-slate-500">若表单还有其它字段要在模板中用 {'{别名}'} 引用，可在此添加（别名仅限字母数字下划线）。</p>
          <div className="space-y-2">
            {extras.map((r) => (
              <div key={r._id} className="flex flex-wrap items-center gap-2">
                <input
                  className={inputClass + ' max-w-[140px] font-mono text-sm'}
                  placeholder="别名"
                  value={r.alias}
                  onChange={(e) =>
                    setExtras((list) => list.map((x) => (x._id === r._id ? { ...x, alias: e.target.value } : x)))
                  }
                />
                <span className="text-slate-400">→</span>
                <input
                  className={`${inputClass} min-w-[160px] flex-1 font-mono text-sm`}
                  placeholder="field_xx"
                  value={r.fieldKey}
                  onChange={(e) =>
                    setExtras((list) => list.map((x) => (x._id === r._id ? { ...x, fieldKey: e.target.value } : x)))
                  }
                />
                <button
                  type="button"
                  className="rounded-lg px-2 py-1.5 text-xs text-rose-600 hover:bg-rose-50"
                  onClick={() => removeExtra(r._id)}
                >
                  删除
                </button>
              </div>
            ))}
          </div>
          <Button type="button" variant="secondary" className="mt-3" size="sm" onClick={addExtraRow}>
            添加一行
          </Button>
        </div>
      </Card>

      <Card title="草稿标题">
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/70 p-3">
          <p className="mb-2 text-xs text-slate-600">预设模板（一键填充，可再手动微调）</p>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className={`${inputClass} min-w-[180px]`}
              value={draftTitlePreset}
              onChange={(e) => setDraftTitlePreset((e.target.value as 'resale' | 'hiring' | 'marriage') || 'resale')}
            >
              <option value="resale">二手交易版</option>
              <option value="hiring">招聘版</option>
              <option value="marriage">征婚版</option>
            </select>
            <Button type="button" variant="secondary" onClick={() => applyTitlePreset('draft', draftTitlePreset)}>
              应用模板
            </Button>
          </div>
        </div>
        <fieldset className="mb-4 space-y-2 border-0 p-0">
          <legend className="mb-2 text-sm font-medium text-slate-800">标题生成方式</legend>
          <label className="flex cursor-pointer items-start gap-2">
            <input
              type="radio"
              name="draft_title_mode"
              className="mt-1 h-4 w-4 border-slate-300 text-teal-600 focus:ring-teal-500"
              checked={titleMode === 'template'}
              onChange={() => setTitleMode('template')}
            />
            <span>
              <span className="font-medium text-slate-800">固定格式（模板）</span>
              <span className="mt-0.5 block text-xs text-slate-500">与原先一致，用下方占位符渲染标题。</span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2">
            <input
              type="radio"
              name="draft_title_mode"
              className="mt-1 h-4 w-4 border-slate-300 text-teal-600 focus:ring-teal-500"
              checked={titleMode === 'ai'}
              onChange={() => setTitleMode('ai')}
            />
            <span>
              <span className="font-medium text-slate-800">AI 写标题</span>
              <span className="mt-0.5 block text-xs text-slate-500">
                使用大模型从正文等字段抽取核心信息生成短标题（系统已内置规则：地点、物品、数量、价格等；**不超过 20 字**）。请先在
                <Link to="/ai-engines" className="mx-0.5 font-medium text-teal-800 underline">
                  AI 接口引擎
                </Link>
                配置大模型 API；各站点可在「接入与推送 → 自动化」或创建站点时选择使用哪一套。
              </span>
            </span>
          </label>
        </fieldset>

        {titleMode === 'template' ? (
          <>
            <label className={labelClass}>
              <span>标题模板</span>
              <input
                className={inputClass}
                value={titleTpl}
                onChange={(e) => setTitleTpl(e.target.value)}
                placeholder="例如 【贴图】{date}"
              />
            </label>
            <p className="mt-2 text-xs text-slate-500">
              可使用占位符 <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[11px]">{'{date}'}</code>
              ，渲染为中文短日期（如 26年4月15日，优先取展示起始日，否则取提交时间）。亦可在模板中继续使用已映射字段，如{' '}
              <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[11px]">{'{body}'}</code> 等。
            </p>
          </>
        ) : (
          <div className="space-y-3">
            <p className={`${settingsPanelClass} text-xs text-slate-600 leading-relaxed`}>
              <span className="font-medium text-slate-800">系统内置效果（无需重复填写）</span>
              ：从正文等字段抽取核心内容，例如原文含「玉兰花园」「不锈钢餐盘」「50 个」「8 元一个」时，生成标题可接近
              <code className="mx-0.5 rounded bg-white px-1 font-mono text-[11px]">玉兰花园：餐盘50个(8元/个)</code>
              这类形式（有则写、无则不编）。<strong className="text-slate-800">字数硬限制 20 字以内</strong>。
            </p>
            <label className={labelClass}>
              <span>补充提示词（可选）</span>
              <textarea
                className={textareaClass + ' min-h-[100px] text-sm'}
                value={titleAiPrompt}
                onChange={(e) => setTitleAiPrompt(e.target.value)}
                placeholder={TITLE_AI_EXTRA_PLACEHOLDER}
              />
            </label>
            <p className="text-xs text-slate-500">
              AI 失败时会自动回退为下方「固定格式」模板，建议仍保留模板内容作备用。
            </p>
            <label className={labelClass}>
              <span>回退用标题模板（AI 失败时）</span>
              <input
                className={inputClass}
                value={titleTpl}
                onChange={(e) => setTitleTpl(e.target.value)}
                placeholder="例如 【贴图】{date}"
              />
            </label>
          </div>
        )}
      </Card>

      <Card title="信息标题">
        <p className="mb-3 text-xs text-slate-500">
          用于<strong className="text-slate-700">信息进入贴图虾系统入库时</strong>补全标题（仅在原始数据无标题时触发），不影响你后续在管理登记中手工修改。
        </p>
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/70 p-3">
          <p className="mb-2 text-xs text-slate-600">预设模板（一键填充，可再手动微调）</p>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className={`${inputClass} min-w-[180px]`}
              value={entryTitlePreset}
              onChange={(e) => setEntryTitlePreset((e.target.value as 'resale' | 'hiring' | 'marriage') || 'resale')}
            >
              <option value="resale">二手交易版</option>
              <option value="hiring">招聘版</option>
              <option value="marriage">征婚版</option>
            </select>
            <Button type="button" variant="secondary" onClick={() => applyTitlePreset('entry', entryTitlePreset)}>
              应用模板
            </Button>
          </div>
        </div>
        <fieldset className="mb-4 space-y-2 border-0 p-0">
          <legend className="mb-2 text-sm font-medium text-slate-800">标题生成方式</legend>
          <label className="flex cursor-pointer items-start gap-2">
            <input
              type="radio"
              name="entry_title_mode"
              className="mt-1 h-4 w-4 border-slate-300 text-teal-600 focus:ring-teal-500"
              checked={entryTitleMode === 'template'}
              onChange={() => setEntryTitleMode('template')}
            />
            <span>
              <span className="font-medium text-slate-800">固定格式（模板）</span>
              <span className="mt-0.5 block text-xs text-slate-500">信息入库时按模板渲染并截断到 20 字。</span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2">
            <input
              type="radio"
              name="entry_title_mode"
              className="mt-1 h-4 w-4 border-slate-300 text-teal-600 focus:ring-teal-500"
              checked={entryTitleMode === 'ai'}
              onChange={() => setEntryTitleMode('ai')}
            />
            <span>
              <span className="font-medium text-slate-800">AI 写标题</span>
              <span className="mt-0.5 block text-xs text-slate-500">
                与草稿标题一致，按正文抽取地点/物品/数量/价格等核心信息，控制在 20 字内。
              </span>
            </span>
          </label>
        </fieldset>
        {entryTitleMode === 'template' ? (
          <>
            <label className={labelClass}>
              <span>信息标题模板</span>
              <input
                className={inputClass}
                value={entryTitleTpl}
                onChange={(e) => setEntryTitleTpl(e.target.value)}
                placeholder="例如 {body}"
              />
            </label>
            <p className="mt-2 text-xs text-slate-500">
              可使用 <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[11px]">{'{body}'}</code>、
              <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[11px]">{'{date}'}</code>、
              <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[11px]">{'{form_name}'}</code> 等变量。
            </p>
          </>
        ) : (
          <div className="space-y-3">
            <label className={labelClass}>
              <span>补充提示词（可选）</span>
              <textarea
                className={textareaClass + ' min-h-[100px] text-sm'}
                value={entryTitleAiPrompt}
                onChange={(e) => setEntryTitleAiPrompt(e.target.value)}
                placeholder={TITLE_AI_EXTRA_PLACEHOLDER}
              />
            </label>
            <label className={labelClass}>
              <span>回退模板（AI 失败时）</span>
              <input
                className={inputClass}
                value={entryTitleTpl}
                onChange={(e) => setEntryTitleTpl(e.target.value)}
                placeholder="例如 {body}"
              />
            </label>
          </div>
        )}
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/70 p-3">
          <p className="mb-2 text-xs text-slate-600">历史补全：将当前站点“标题为空”的历史信息按上方配置补生成标题。</p>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs text-slate-600">
              单次处理上限
              <input
                type="number"
                min={1}
                max={1000}
                className={`${inputClass} mt-1 w-28`}
                value={backfillLimit}
                onChange={(e) => setBackfillLimit(Math.min(1000, Math.max(1, Number(e.target.value) || 200)))}
              />
            </label>
            <Button type="button" variant="secondary" disabled={backfillBusy || currentSiteId < 1} onClick={() => void backfillEntryTitles()}>
              {backfillBusy ? '补全中…' : '补全历史缺失标题'}
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={backfillAllBusy || currentSiteId < 1}
              onClick={() => void backfillEntryTitlesAll()}
            >
              {backfillAllBusy ? '全量补全中…' : '全量补全直到完成'}
            </Button>
          </div>
          <div className="mt-3 flex flex-wrap items-end gap-2 border-t border-slate-200 pt-3">
            <label className="text-xs text-slate-600">
              指定 entry_id
              <input
                type="number"
                min={1}
                className={`${inputClass} mt-1 w-32`}
                value={singleBackfillEntryId || ''}
                onChange={(e) => setSingleBackfillEntryId(Math.max(0, Number(e.target.value) || 0))}
                placeholder="例如 123"
              />
            </label>
            <Button
              type="button"
              variant="secondary"
              disabled={singleBackfillBusy || currentSiteId < 1}
              onClick={() => void backfillSingleEntryTitle()}
            >
              {singleBackfillBusy ? '单条补全中…' : '按 entry_id 补单条标题'}
            </Button>
          </div>
          <div className="mt-3">
            <label className="mb-1 block text-xs text-slate-600">执行日志（含 AI/模板/兜底轨迹）</label>
            <textarea
              className={`${inputClass} min-h-[180px] font-mono text-[11px]`}
              value={execLogs.join('\n')}
              readOnly
              placeholder="点击补全按钮后，这里会显示处理进度与轨迹日志。"
            />
          </div>
        </div>
      </Card>

      {miniprogramsForDraft.length > 0 ? (
        <Card title="公众号草稿：关联小程序">
          <p className="mb-3 text-xs text-slate-600">
            当某小程序在「小程序管理」中勾选了本站点，且在该小程序里填写了「公众号草稿：小程序 AppID」后，推送到公众号草稿时可为每条信息生成进入小程序详情页的路径，并在模板中使用下列占位符。
          </p>
          <label className={labelClass}>
            <span>草稿中小程序链使用哪个小程序</span>
            <select
              className={inputClass + ' max-w-xl'}
              value={draftMiniprogramId}
              onChange={(e) => setDraftMiniprogramId(Math.max(0, parseInt(e.target.value, 10) || 0))}
            >
              <option value={0}>自动（启用且已填 AppID 的候选中，取 id 最小的一个）</option>
              {miniprogramsForDraft.map((m) => (
                <option key={m.id} value={m.id} disabled={m.enabled !== 1 || !m.appid_ready}>
                  {m.name}（id={m.id}
                  {m.enabled !== 1 ? '，已停用' : ''}
                  {!m.appid_ready ? '，未配置 AppID' : ''}）
                </option>
              ))}
            </select>
          </label>
          <label className={`${labelClass} mt-4 block`}>
            <span>小程序文字链可见文案</span>
            <input
              className={`${inputClass} mt-1 max-w-xl`}
              value={draftMiniprogramAnchorText}
              onChange={(e) => setDraftMiniprogramAnchorText(e.target.value)}
              maxLength={128}
              placeholder="留空则默认「打开小程序查看本条」；例如：☎️点此·联系我"
              spellCheck={false}
            />
            <span className="mt-1 block text-xs text-slate-500">
              用于公众号草稿里小程序入口上显示的文字；模板变量 <code className="rounded bg-slate-50 px-1">{`{miniprogram_link_text}`}</code> 与文末自动附加的链接文案与此一致（最多 128 字）。
            </span>
          </label>
          <p className="mt-3 text-xs text-slate-500">
            模板变量：<code className="rounded bg-slate-50 px-1">{`{miniprogram_appid}`}</code>、
            <code className="rounded bg-slate-50 px-1">{`{miniprogram_path}`}</code>（如 pages/detail/detail?id=与条目 id 一致）、
            <code className="rounded bg-slate-50 px-1">{`{miniprogram_name}`}</code>、
            <code className="rounded bg-slate-50 px-1">{`{miniprogram_link_text}`}</code>（与上方「可见文案」一致）、
            <code className="rounded bg-slate-50 px-1">{`{entry_id}`}</code>。
            普通图文可插入 <code className="rounded bg-slate-50 px-1">{`{miniprogram_link_html}`}</code>
            （微信官方要求的带 <code className="rounded bg-slate-50 px-1">data-miniprogram-appid</code> 的 <code className="rounded bg-slate-50 px-1">{'<a>'}</code> 片段）。
            贴图体裁下模板会先转纯文本，因此 <code className="rounded bg-slate-50 px-1">{`{miniprogram_link_html}`}</code> 在模板中请留空；系统在正文末尾自动附加同一段小程序文字链（须公众号与小程序已关联）。
          </p>
        </Card>
      ) : null}

      <Card
        title={
          draftMode === 'newspic'
            ? '草稿正文模板（HTML，贴图下会转为纯文本）'
            : '草稿正文 HTML 模板'
        }
      >
        <textarea
          className={textareaClass + ' min-h-[200px] font-mono text-xs'}
          value={htmlTpl}
          onChange={(e) => setHtmlTpl(e.target.value)}
        />
        <p className="mt-2 text-xs text-slate-500">
          {draftMode === 'newspic'
            ? '贴图体裁的正文不支持 HTML，写入草稿前会去掉标签并保留换行，总长度约 1 万字以内。若已配置关联小程序，文末会自动追加小程序入口（与 {miniprogram_link_html} 内容一致）。'
            : '普通图文保留 HTML；正文内图片须来自微信公众平台「上传图文消息内的图片」接口返回的 URL，否则可能被过滤。'}
        </p>
      </Card>

      <Card title="展示有效期（天）">
        <label className={labelClass}>
          <span>默认天数</span>
          <input
            type="number"
            min={1}
            className={inputClass + ' max-w-xs'}
            value={days}
            onChange={(e) => setDays(Math.max(1, parseInt(e.target.value, 10) || 7))}
          />
        </label>
        <p className="mt-2 text-xs text-slate-500">起止日期以金数据提交日为准。</p>
      </Card>

      <Card title="内容安全：敏感词">
        <p className="mb-2 text-xs text-slate-500">
          一行一词；标题与正文中只要包含（忽略大小写）即视为命中。命中时金数据自动流不会直推微信草稿，需在「管理登记」中确认或改稿后再点「推送到公众号草稿箱」。与「接入与推送 → 流程与开关」中「人工审核」的关系见该页说明。也可在{' '}
          <Link to="/interfaces#sensitive" className="font-semibold text-teal-800 underline underline-offset-2">
            接入与推送 → 敏感词管理
          </Link>{' '}
          单独维护同一词表。
        </p>
        <label className={labelClass}>
          <span>词表</span>
          <textarea
            className={textareaClass + ' min-h-[120px] font-mono text-xs'}
            value={sensitiveWords}
            onChange={(e) => setSensitiveWords(e.target.value)}
            placeholder={'每行一个关键词或短语，例如：\n微信\n测试词'}
            spellCheck={false}
          />
        </label>
      </Card>

      <details
        className="rounded-xl border border-slate-200 bg-white p-4"
        onToggle={(e) => {
          const el = e.currentTarget;
          const o = el.open;
          setAdvancedOpen(o);
          if (o) {
            setJsonOverride(JSON.stringify(stateToFieldMap(fixed, extras), null, 2));
          }
        }}
      >
        <summary className="cursor-pointer text-sm font-medium text-slate-700">高级：直接编辑 JSON（展开后以本框为准保存）</summary>
        <p className="mt-2 text-xs text-amber-800">
          展开后保存时优先使用下方 JSON；折叠后保存则使用上方表单。
        </p>
        <textarea
          className={textareaClass + ' mt-2 min-h-[140px] font-mono text-xs'}
          value={jsonOverride}
          onChange={(e) => setJsonOverride(e.target.value)}
          spellCheck={false}
        />
      </details>

      <div className="flex flex-wrap gap-2">
        <Button onClick={() => void save()} disabled={saving || currentSiteId < 1}>
          {saving ? '保存中…' : '保存当前站点'}
        </Button>
      </div>
      {msg ? <p className="text-sm text-emerald-700">{msg}</p> : null}
      {err ? <p className="text-sm text-rose-600">{err}</p> : null}
        </>
      ) : null}
    </div>
  );
}
